import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState({
    companyName: 'ALG Garantia',
    primaryColor: '#2563EB',
    secondaryColor: '#1E40AF',
    accentColor: '#3B82F6',
    sidebarBgColor: '#1E293B',
    sidebarTextColor: '#F8FAFC',
    loginTitle: 'Gestão profissional de garantias',
    loginSubtitle: '',
    logoUrl: null,
    faviconUrl: null,
    loaded: false,
  });

  useEffect(() => {
    loadTheme();
  }, []);

  // Recarregar tema quando a URL mudar (troca de slug)
  useEffect(() => {
    const handleLocationChange = () => {
      loadTheme();
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const loadTheme = async () => {
    try {
      // Extrair domínio e slug do path
      const domain = window.location.hostname;
      const pathname = window.location.pathname;
      
      // Extrair slug do path (garantia.algti.com/elo/login -> slug: "elo")
      const pathParts = pathname.split('/').filter(p => p);
      
      // Ignorar rotas públicas e do sistema
      const systemRoutes = ['consulta', 'login'];
      let slug = null;
      
      if (pathParts.length > 0 && !systemRoutes.includes(pathParts[0])) {
        slug = pathParts[0];
      }
      
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('🎨 Carregando tema:', { domain, pathname, slug });
      }
      
      // Buscar layout do backend (endpoint público)
      const params = { domain };
      if (slug) {
        params.slug = slug;
      }
      
      const { data } = await api.get('/public/layout', { params });
      
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Tema carregado:', data);
      }

      if (data) {
        setTheme({
          ...data,
          loaded: true,
        });

        // Aplicar cores CSS
        applyTheme(data);

        // Aplicar favicon
        if (data.faviconUrl) {
          updateFavicon(data.faviconUrl);
        }

        // Atualizar título da página
        document.title = `${data.companyName} - Gestão de Garantia`;
      }
    } catch (error) {
      // Apenas logar erro se não for localhost (desenvolvimento)
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isLocalhost) {
        console.error('Erro ao carregar tema:', error);
      }
      // Manter tema padrão
      setTheme(prev => ({ ...prev, loaded: true }));
    }
  };

  const applyTheme = (themeData) => {
    const root = document.documentElement;
    
    root.style.setProperty('--primary-color', themeData.primaryColor);
    root.style.setProperty('--secondary-color', themeData.secondaryColor);
    root.style.setProperty('--accent-color', themeData.accentColor);
    root.style.setProperty('--sidebar-bg-color', themeData.sidebarBgColor);
    root.style.setProperty('--sidebar-text-color', themeData.sidebarTextColor);
  };

  const updateFavicon = (faviconUrl) => {
    const apiUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://api.algti.com');
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = `${apiUrl}${faviconUrl}`;
    document.getElementsByTagName('head')[0].appendChild(link);
  };

  return (
    <ThemeContext.Provider value={{ theme, loadTheme, applyTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
